/**
 * Page Section Type Registry - Client-side mirror of storefront registry
 *
 * Defines available section types and their prop schemas for the builder UI.
 */

import { z } from 'zod';

export interface SectionTypeDefinition {
  type: string;
  label: string;
  description: string;
  propsSchema: z.ZodObject<any>;
  defaultProps: Record<string, unknown>;
}

/**
 * Product grid section props schema.
 */
const ProductGridPropsSchema = z.object({
  limit: z.number().int().positive().max(50).optional().default(8),
});

/**
 * Categories section props schema.
 */
const CategoriesPropsSchema = z.object({
  parentHandle: z.string().optional(),
});

/**
 * Hero section props schema (currently static, but can be extended).
 */
const HeroPropsSchema = z.object({}).passthrough();

/**
 * Announcement bar section props schema.
 */
const AnnouncementBarPropsSchema = z.object({
  message: z.string().optional(),
  linkText: z.string().optional(),
  linkHref: z.string().optional(),
});

/**
 * Text block section props schema.
 */
const TextBlockPropsSchema = z.object({
  heading: z.string().optional(),
  body: z.string().optional().default(''),
  alignment: z.enum(['left', 'center', 'right']).optional().default('left'),
});

export const SECTION_TYPES: SectionTypeDefinition[] = [
  {
    type: 'hero',
    label: 'Hero Banner',
    description: 'Large banner section with call to action',
    propsSchema: HeroPropsSchema,
    defaultProps: {},
  },
  {
    type: 'product-grid',
    label: 'Product Grid',
    description: 'Grid of featured products',
    propsSchema: ProductGridPropsSchema,
    defaultProps: { limit: 8 },
  },
  {
    type: 'category-grid',
    label: 'Category Grid',
    description: 'Grid of product categories',
    propsSchema: CategoriesPropsSchema,
    defaultProps: {},
  },
  {
    type: 'announcement-bar',
    label: 'Announcement Bar',
    description: 'Top banner for announcements',
    propsSchema: AnnouncementBarPropsSchema,
    defaultProps: { message: 'Welcome to our store!' },
  },
  {
    type: 'text-block',
    label: 'Text Block',
    description: 'Rich text content section',
    propsSchema: TextBlockPropsSchema,
    defaultProps: { body: '', alignment: 'left' },
  },
];

export function getSectionType(type: string): SectionTypeDefinition | undefined {
  return SECTION_TYPES.find(s => s.type === type);
}
